import React from 'react';
import Header from '../Header';
import Banner from '../Products/Banner';
import Footer from '../Footer';
import { Link, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarIcon, UserIcon, ClockIcon } from '@heroicons/react/24/outline';

const content = {
  tr: {
    title: 'Blog Detayı',
    description: 'Teknoloji dünyasından detaylı analizler ve uzman görüşleri',
    footerLang: 'tr',
  },
  en: {
    title: 'Blog Detail',
    description: 'Detailed analysis and expert insights from the technology world',
    footerLang: 'en',
  },
};

const BlogDetail: React.FC = () => {
  const location = useLocation();
  const { blogId } = useParams();
  const language = location.pathname.startsWith('/en') ? 'en' : 'tr';
  const lang: 'en' | 'tr' = language;
  const page = content[lang];



  // Her blog yazısı için ayrı içerik (id'ye göre seçilir)
  const para = (tr: string, en: string) => ({ tr, en });
  const makeContent = (paras: { tr: string; en: string }[]) => ({
    tr: paras.map(p => `<p class="mb-6 text-lg leading-relaxed">${p.tr}</p>`).join('\n'),
    en: paras.map(p => `<p class="mb-6 text-lg leading-relaxed">${p.en}</p>`).join('\n'),
  });

  const posts: Record<string, any> = {
    '1': {
      image: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=1200&q=80',
      title: { tr: 'Yapay Zeka ile Dijital Dönüşüm', en: 'Digital Transformation with AI' },
      content: makeContent([
        para('Yapay zeka teknolojileri, iş dünyasında köklü bir dönüşümü tetikliyor. Şirketler; müşteri deneyimini iyileştirmek, operasyonel verimliliği artırmak ve yeni gelir modelleri oluşturmak için yapay zeka çözümlerini hızla benimsiyor.', 'Artificial intelligence is triggering a profound transformation in the business world. Companies are rapidly adopting AI solutions to improve customer experience, increase operational efficiency and build new revenue models.'),
        para('Rutin görevlerin otomatikleştirilmesi, ekiplerin daha stratejik işlere odaklanmasını sağlıyor. Müşteri hizmetlerinden finansal analize kadar her alanda yapay zeka, karar süreçlerini hızlandırıyor ve hata payını azaltıyor.', 'Automating routine tasks lets teams focus on more strategic work. From customer service to financial analysis, AI speeds up decision-making and reduces the margin of error across every domain.'),
        para('Önümüzdeki dönemde doğal dil işleme, bilgisayarlı görü ve üretken yapay zeka alanlarındaki gelişmeler, bu dönüşümü daha da hızlandıracak. Teknolojiyi erken benimseyen kurumlar kalıcı bir rekabet avantajı elde ediyor.', 'In the coming period, advances in natural language processing, computer vision and generative AI will accelerate this transformation further. Organizations that adopt the technology early gain a lasting competitive advantage.'),
      ]),
      author: { tr: 'Ahmet Yılmaz', en: 'Ahmet Yilmaz', title: { tr: 'Yapay Zeka Uzmanı', en: 'AI Specialist' }, avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&q=80' },
      date: '2024-01-15', readTime: { tr: '5 dk okuma', en: '5 min read' }, tags: ['AI', 'Digital Transformation', 'Technology'],
    },
    '2': {
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80',
      title: { tr: 'Bulut Bilişimde Güvenlik', en: 'Security in Cloud Computing' },
      content: makeContent([
        para('Bulut bilişim, ölçeklenebilirlik ve maliyet avantajları sunarken beraberinde yeni güvenlik sorumlulukları getiriyor. Verilerin nerede ve nasıl saklandığını bilmek, güvenli bir bulut altyapısının ilk adımıdır.', 'While cloud computing offers scalability and cost advantages, it also brings new security responsibilities. Knowing where and how data is stored is the first step toward a secure cloud infrastructure.'),
        para('Kimlik ve erişim yönetimi, şifreleme ve düzenli güvenlik denetimleri, bulut ortamlarında veri sızıntılarını önlemenin temelini oluşturur. Paylaşılan sorumluluk modeli, hem sağlayıcının hem de kullanıcının rolünü net biçimde tanımlar.', 'Identity and access management, encryption and regular security audits form the basis of preventing data breaches in cloud environments. The shared responsibility model clearly defines the role of both the provider and the customer.'),
        para('En iyi uygulamalar arasında çok faktörlü kimlik doğrulama, en az yetki ilkesi ve sürekli izleme yer alır. Bu önlemler, tehditlere karşı proaktif bir savunma katmanı sağlar.', 'Best practices include multi-factor authentication, the principle of least privilege and continuous monitoring. These measures provide a proactive layer of defense against threats.'),
      ]),
      author: { tr: 'Mehmet Kaya', en: 'Mehmet Kaya', title: { tr: 'Bulut Güvenliği Uzmanı', en: 'Cloud Security Specialist' }, avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&q=80' },
      date: '2024-01-10', readTime: { tr: '7 dk okuma', en: '7 min read' }, tags: ['Cloud Security', 'Data Protection', 'Best Practices'],
    },
    '3': {
      image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=80',
      title: { tr: 'Mobil Uygulama Geliştirme Trendleri', en: 'Mobile App Development Trends' },
      content: makeContent([
        para('Mobil uygulama geliştirme, kullanıcı beklentilerinin sürekli yükseldiği dinamik bir alan. Hız, akıcı deneyim ve çevrimdışı çalışabilme, günümüz kullanıcılarının temel talepleri arasında.', 'Mobile app development is a dynamic field where user expectations keep rising. Speed, smooth experience and offline capability are among the core demands of today’s users.'),
        para('Çapraz platform çözümleri, tek kod tabanıyla hem iOS hem Android için geliştirme yapmayı mümkün kılıyor. React Native ve Flutter, geliştirme süresini kısaltırken tutarlı bir kullanıcı deneyimi sunuyor.', 'Cross-platform solutions make it possible to develop for both iOS and Android from a single codebase. React Native and Flutter shorten development time while delivering a consistent user experience.'),
        para('Yapay zeka destekli kişiselleştirme, biyometrik güvenlik ve sürükleyici arayüzler, önümüzdeki yıllarda mobil deneyimi şekillendirecek başlıca trendler arasında yer alıyor.', 'AI-powered personalization, biometric security and immersive interfaces are among the leading trends that will shape the mobile experience in the coming years.'),
      ]),
      author: { tr: 'Zeynep Demir', en: 'Zeynep Demir', title: { tr: 'Mobil Geliştirme Lideri', en: 'Mobile Development Lead' }, avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&q=80' },
      date: '2024-01-05', readTime: { tr: '6 dk okuma', en: '6 min read' }, tags: ['Mobile Development', 'React Native', 'Flutter'],
    },
    '4': {
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80',
      title: { tr: 'Siber Güvenlik Tehditleri ve Korunma Yöntemleri', en: 'Cybersecurity Threats and Protection Methods' },
      content: makeContent([
        para('Dijitalleşmenin hızlanmasıyla siber tehditler hem sayı hem de karmaşıklık olarak artıyor. Fidye yazılımları, kimlik avı ve tedarik zinciri saldırıları, kurumların karşılaştığı en yaygın risklerin başında geliyor.', 'As digitalization accelerates, cyber threats are increasing in both number and complexity. Ransomware, phishing and supply chain attacks are among the most common risks organizations face.'),
        para('Etkili bir savunma, teknolojiyi ve insan farkındalığını birlikte ele almayı gerektirir. Çalışan eğitimleri, güncel yamalar ve güçlü erişim politikaları, saldırı yüzeyini önemli ölçüde daraltır.', 'An effective defense requires addressing both technology and human awareness. Employee training, up-to-date patches and strong access policies significantly reduce the attack surface.'),
        para('Olay müdahale planları ve düzenli sızma testleri, bir saldırı gerçekleştiğinde hasarı en aza indirmenin anahtarıdır. Güvenlik, tek seferlik bir proje değil sürekli bir süreçtir.', 'Incident response plans and regular penetration tests are key to minimizing damage when an attack occurs. Security is not a one-off project but a continuous process.'),
      ]),
      author: { tr: 'Can Özkan', en: 'Can Ozkan', title: { tr: 'Siber Güvenlik Danışmanı', en: 'Cybersecurity Consultant' }, avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80' },
      date: '2023-12-28', readTime: { tr: '8 dk okuma', en: '8 min read' }, tags: ['Cybersecurity', 'Threat Protection', 'Security Strategy'],
    },
    '5': {
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80',
      title: { tr: 'Kurumsal Dijital Dönüşüm Stratejileri', en: 'Corporate Digital Transformation Strategies' },
      content: makeContent([
        para('Dijital dönüşüm, yalnızca yeni teknolojiler edinmek değil; iş modelini, kültürü ve süreçleri yeniden tasarlamaktır. Başarılı dönüşüm, net bir vizyon ve üst yönetimin kararlı desteğiyle başlar.', 'Digital transformation is not merely about acquiring new technologies; it is about redesigning the business model, culture and processes. Successful transformation starts with a clear vision and committed support from senior management.'),
        para('Veriye dayalı karar alma, süreç otomasyonu ve müşteri odaklı hizmet tasarımı, dönüşümün temel yapı taşlarıdır. Kurumlar, olgunluk seviyelerini ölçerek önceliklerini belirlemelidir.', 'Data-driven decision-making, process automation and customer-centric service design are the building blocks of transformation. Organizations should assess their maturity level to set priorities.'),
        para('Değişime direnci azaltmak için çalışanların sürece dahil edilmesi kritik önem taşır. Küçük ama ölçülebilir kazanımlarla ilerlemek, dönüşümü sürdürülebilir kılar.', 'Involving employees in the process is critical to reducing resistance to change. Progressing with small but measurable wins makes the transformation sustainable.'),
      ]),
      author: { tr: 'Elif Yıldız', en: 'Elif Yildiz', title: { tr: 'Dijital Strateji Danışmanı', en: 'Digital Strategy Advisor' }, avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&q=80' },
      date: '2023-12-20', readTime: { tr: '10 dk okuma', en: '10 min read' }, tags: ['Digital Transformation', 'Corporate Strategy', 'Innovation'],
    },
    '6': {
      image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?auto=format&fit=crop&w=1200&q=80',
      title: { tr: 'Makine Öğrenmesi Uygulamaları', en: 'Machine Learning Applications' },
      content: makeContent([
        para('Makine öğrenmesi, verilerden örüntüler çıkararak tahmin ve sınıflandırma yapabilen sistemlerin temelini oluşturuyor. Bugün finanstan sağlığa, perakendeden üretime kadar birçok sektörde yaygın olarak kullanılıyor.', 'Machine learning forms the basis of systems that can predict and classify by extracting patterns from data. Today it is widely used across many sectors, from finance to healthcare, retail to manufacturing.'),
        para('Öneri sistemleri, dolandırıcılık tespiti ve talep tahmini, makine öğrenmesinin en somut iş uygulamalarından bazılarıdır. Model kalitesi, büyük ölçüde verinin kalitesine ve doğru etiketlenmesine bağlıdır.', 'Recommendation systems, fraud detection and demand forecasting are among the most concrete business applications of machine learning. Model quality depends largely on the quality of the data and correct labeling.'),
        para('Modelleri üretime almak kadar onları izlemek ve zamanla yeniden eğitmek de önemlidir. Sürdürülebilir bir makine öğrenmesi pratiği, sağlam bir veri altyapısı ve disiplinli süreçler gerektirir.', 'Deploying models to production is important, but so is monitoring and retraining them over time. A sustainable machine learning practice requires a solid data infrastructure and disciplined processes.'),
      ]),
      author: { tr: 'Burak Arslan', en: 'Burak Arslan', title: { tr: 'Veri Bilimci', en: 'Data Scientist' }, avatar: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?auto=format&fit=crop&w=100&q=80' },
      date: '2023-12-15', readTime: { tr: '9 dk okuma', en: '9 min read' }, tags: ['Machine Learning', 'AI Applications', 'Data Science'],
    },
  };

  const blog = posts[blogId ?? '1'] ?? posts['1'];



  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const breadcrumb = (
    <div className="mt-1 mb-2 text-sm flex items-center space-x-2">
      <Link to={`/${lang}`} className="text-gray-400 font-bold">{lang === 'tr' ? 'Ana Sayfa' : 'Home'}</Link>
      <span className="text-gray-400 font-bold">/</span>
      <Link to={`/${lang}/blog`} className="text-gray-400 font-bold">{lang === 'tr' ? 'Blog' : 'Blog'}</Link>
      <span className="text-gray-400 font-bold">/</span>
      <span className="text-white font-bold">{blog.title[lang].substring(0, 30)}...</span>
    </div>
  );

  return (
    <div>
      <Header language={lang} setLanguage={() => {}} />
      <Banner
        title={page.title}
        description={page.description}
        image={process.env.PUBLIC_URL + '/banner.png'}
        breadcrumb={breadcrumb}
      />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Blog Başlığı ve Meta Bilgiler */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {blog.title[lang]}
          </h1>
          
          {/* Meta Bilgiler */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              <span>{blog.author[lang]}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              <span>{formatDate(blog.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <ClockIcon className="h-5 w-5" />
              <span>{blog.readTime[lang]}</span>
            </div>
          </div>

          {/* Etiketler */}
          <div className="flex flex-wrap gap-2 mb-6">
            {blog.tags.map((tag: string, idx: number) => (
              <span
                key={idx}
                className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Blog Sayfasına Dönüş Butonu */}
          <div className="mb-8">
            <Link
              to={`/${lang}/blog`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {lang === 'tr' ? 'Blog Sayfasına Dön' : 'Back to Blog'}
            </Link>
          </div>
        </motion.div>

        {/* Blog Görseli */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <img
            src={blog.image}
            alt={blog.title[lang]}
            className="w-full h-96 object-cover rounded-xl shadow-lg"
          />
        </motion.div>

        {/* Blog İçeriği */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="prose prose-lg max-w-none"
        >
          <div 
            dangerouslySetInnerHTML={{ __html: blog.content[lang] }}
            className="text-gray-700 leading-relaxed"
          />
        </motion.div>

        {/* Yazar Bilgisi */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 p-6 bg-gray-50 rounded-xl"
        >
          <div className="flex items-center gap-4">
            <img
              src={blog.author.avatar}
              alt={blog.author[lang]}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{blog.author[lang]}</h3>
              <p className="text-gray-600">{blog.author.title[lang]}</p>
              <p className="text-sm text-gray-500 mt-1">
                {lang === 'tr' 
                  ? 'Teknoloji ve yapay zeka alanında uzman. 10+ yıl deneyim.'
                  : 'Expert in technology and artificial intelligence. 10+ years experience.'
                }
              </p>
            </div>
          </div>
        </motion.div>


      </div>

      <Footer language={lang} />
    </div>
  );
};

export default BlogDetail; 